import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import i18n from 'simple-react-i18n'
import { maxBy, minBy, partition, round } from 'lodash'
import PluviometryAction from '../../../actions/PluviometryAction'
import PluviometerDto from '../../../dto/PluviometerDto'
import EChartV6Pluvio from '../../../../components/echart/EChartV6Pluvio'
import {
    exportExcelIcon,
    exportPictureIcon,
    getAxisIntervalFormatter,
} from '../../../../components/echart/EChartUtils'
import MessageCard from '../../../../components/card/MessageCard'
import ProgressCard from '../../../../components/card/ProgressCard'
import { StyledFieldSet, StyledLegend } from '../../../../components/StyledElements'
import { getFullDate, getMonthYear, getYear, getYearDate } from '../../../../utils/DateUtil'
import { exportFile } from '../../../../utils/ExportDataUtil'
import { DEFROUNDVALUE } from '../../../../utils/NumberUtil'
import { getExportFormat } from '../../../../utils/StringUtil'
import { DATATYPE_RAIN } from '../../../../utils/PluviometryUtils'
import { Grid } from '@mui/material'

const MONTHS = 'MONTHS'
const YEARS = 'YEARS'
const NORMALE_MONTH = 'NORMALE_TOZERO_MONTH'
const NORMALE_YEAR = 'NORMALE_TOZERO_YEAR'
const CHART_HEIGHT = 400

const getGroupFunc = typeGroupFunc => (typeGroupFunc === YEARS ? NORMALE_YEAR : NORMALE_MONTH)

const formatTooltipDate = (date, typeGroupFunc) => {
    if (typeGroupFunc === YEARS) return getYearDate(date)
    if (typeGroupFunc === MONTHS) return getMonthYear(date)
    return getFullDate(date)
}

const NormalsGraphPluvio = ({
    pluviometer,
    typeGroupFunc = MONTHS,
}) => {
    const [dataLoaded, setDataLoaded] = useState(false)
    const [progress, setProgress] = useState(0)
    const maxDate = moment().valueOf()

    const dispatch = useDispatch()
    const pluvioMeasures = useSelector(state => state.PluviometryReducer.pluvioMeasures)
    const pluviometerStatistics = useSelector(state => state.PluviometryReducer.pluviometerStatistics)
    const pluviometryDataTypes = useSelector(state => state.PluviometryReducer.pluviometryDataTypes)

    useEffect(() => {
        setDataLoaded(false)
        const input = {
            stationId: pluviometer.id,
            dataType: DATATYPE_RAIN,
            groupFunc: getGroupFunc(typeGroupFunc),
            chartMode: true,
        }
        dispatch(PluviometryAction.loadPluvioChronicMeasures([input], setProgress))
            .then(() => setDataLoaded(true))
    }, [typeGroupFunc])

    const unit = pluviometerStatistics.find(p => p.typeId === DATATYPE_RAIN)?.unit
    const unitLabel = unit ? `[${unit}]` : ''
    const roundValue = pluviometryDataTypes.find(pdt => pdt.id === DATATYPE_RAIN)?.numberDecimal || DEFROUNDVALUE
    const measures = pluvioMeasures[0]?.measures ?? []

    const measuresExport = useMemo(() => measures
        .filter(d => d.date >= measures[0]?.date && d.date <= maxDate)
        .map(d => ({
            stationCode: { value: pluviometer.code },
            stationName: { value: pluviometer.name },
            date: { value: getFullDate(d.date), format: 'dd/MM/yyyy HH:mm:ss', cellType: 'date' },
            value: { value: round(d.value, 2), format: getExportFormat(roundValue), cellType: 'number' },
            type: { value: typeGroupFunc === MONTHS ? i18n.normalMonths : i18n.normalYears },
            headers: ['stationCode', 'stationName', 'date', 'value', 'type'],
        })),
    [measures, maxDate, pluviometer.code, pluviometer.name, typeGroupFunc, roundValue])

    if (!dataLoaded) {
        return (
            <div style={{ marginTop: '15px' }}>
                <ProgressCard progress={progress} />
            </div>
        )
    }

    if (!measures.length) {
        return (
            <div style={{ marginTop: 20 }}>
                <MessageCard>{i18n.noData}</MessageCard>
            </div>
        )
    }

    const [positive, negative] = partition(measures, m => m.value > 0)
    const barWidth = typeGroupFunc === YEARS ? 4 : 2
    const axisLabelObj = getAxisIntervalFormatter(moment(), 0)

    const buildBar = (subset, color) => ({
        name: i18n.rain,
        type: 'bar',
        barWidth,
        itemStyle: { color },
        data: subset.map(m => [m.date, m.value]),
    })

    const option = {
        title: {
            text: `[${pluviometer.code}]${pluviometer.name ? `   -   [${pluviometer.name}]` : ''}`,
            left: 'center',
            top: 5,
        },
        tooltip: {
            trigger: 'axis',
            formatter: params => {
                const param = params[0]
                const [date, value] = param.value
                return `${formatTooltipDate(date, typeGroupFunc)}<br />${param.marker} ${param.seriesName} : ${round(value, 2)} ${i18n.milimetersSymbol}`
            },
        },
        grid: {
            top: 45,
            right: '10%',
            height: CHART_HEIGHT - 50,
            left: 120,
        },
        xAxis: [{
            type: 'time',
            position: 'bottom',
            min: measures[0].date,
            max: maxDate,
            axisLine: { show: true },
            axisTick: { show: true },
            splitLine: { show: true },
            axisLabel: { show: true, formatter: v => String(axisLabelObj.formatter(v)) },
        }],
        yAxis: [{
            type: 'value',
            name: `${i18n.comparisonNormal} ${unitLabel}`,
            nameLocation: 'middle',
            nameGap: 40,
            splitLine: { show: true },
        }],
        axisPointer: { link: [{ xAxisIndex: 'all' }] },
        toolbox: {
            show: true,
            right: 65,
            feature: {
                restore: { title: i18n.restore },
                saveAsImage: { title: i18n.export, icon: exportPictureIcon },
                myToolExport: {
                    show: true,
                    title: i18n.excelExport,
                    icon: exportExcelIcon,
                    onclick: () => exportFile({
                        data: measuresExport,
                        exportType: 'xlsx',
                        titleFile: i18n.overview,
                    }),
                },
            },
        },
        series: [buildBar(positive, 'blue'), buildBar(negative, 'red')],
    }

    const maxMeasure = maxBy(measures, 'value')
    const minMeasure = minBy(measures, 'value')
    const maxLabel = typeGroupFunc === MONTHS ? i18n.monthlyMax : i18n.yearsMax
    const minLabel = typeGroupFunc === MONTHS ? i18n.monthlyMin : i18n.yearsMin

    return (
        <Grid sx={{ marginTop: '15px' }}>
            <EChartV6Pluvio option={option} style={{ height: CHART_HEIGHT }} />
            <StyledFieldSet>
                <StyledLegend>{i18n.statistics}</StyledLegend>
                <Grid sx={{ padding: '5px' }}>
                    <>{maxLabel} : {maxMeasure.value} {i18n.milimetersSymbol} - {getYear(maxMeasure.date)}</>
                    <div className='divider' />
                    <>{minLabel} : {minMeasure.value} {i18n.milimetersSymbol} - {getYear(minMeasure.date)}</>
                </Grid>
            </StyledFieldSet>
        </Grid>
    )
}

NormalsGraphPluvio.propTypes = {
    pluviometer: PropTypes.instanceOf(PluviometerDto),
    typeGroupFunc: PropTypes.string,
}

export default NormalsGraphPluvio
